import React from 'react';

import { useLoginBrand } from '../useLoginBrand';
import { liveLegalDestinations } from '../authLegalDestinations';
import LegalDocumentLink from './LegalDocumentLink';

/** Product name of the platform itself, independent of any workspace brand. */
const PLATFORM_NAME = 'Octopus';

/**
 * Who provides the page the visitor is about to type a password into.
 *
 * Octopus is multi-tenant and the auth screens carry per-workspace branding
 * (`?ws=<uuid>` → `useLoginBrand`), so a prospect can land on a sign-in page
 * wearing a charity's logo and colours. The single most important thing this
 * footer says is therefore: the ORGANISATION is the workspace, the PLATFORM is
 * Octopus, and they are not the same party. The branded line says so in as many
 * words; the unbranded line simply attributes the page and never degenerates
 * into "Octopus is provided by Octopus".
 *
 * Exported for testing — it is the copy the whole footer exists to deliver.
 */
export const authAttributionLine = (
  organisationName?: string | null
): string => {
  const name = organisationName?.trim();
  if (!name) {
    return `This sign-in page is provided by ${PLATFORM_NAME}.`;
  }
  return `${name} runs on ${PLATFORM_NAME}. This sign-in page is provided by ${PLATFORM_NAME}, not by ${name}.`;
};

/** "Octopus, © 2026" — the year is read from the clock, never hard-coded. */
export const authCopyrightLine = (now: Date = new Date()): string =>
  `${PLATFORM_NAME}, © ${now.getFullYear()}`;

/**
 * Legal footer for the authentication screens.
 *
 * Modelled on the block a bank puts beneath its sign-in form: who provides the
 * service, the copyright, and the legal links. It is rendered as a SIBLING of
 * the form card — never inside it — and only on the auth family of pages, so
 * the signed-in application is unaffected.
 *
 * Like `LoginSidePanel`, it self-resolves the pre-auth workspace brand from
 * `?ws=` via `useLoginBrand`, so every auth page gets consistent attribution
 * with no per-page wiring.
 *
 * The link row renders ONLY destinations verified to resolve. Today that set is
 * empty — see the verification log in `authLegalDestinations.ts` — so no row is
 * drawn at all. Shipping a link to a host that does not exist would be worse
 * than shipping no link.
 */
const AuthLegalFooter = () => {
  const loginBrand = useLoginBrand();
  const organisationName = loginBrand.branded ? loginBrand.name : null;
  const destinations = liveLegalDestinations();

  return (
    <footer
      aria-label="Legal information"
      className="mt-10 w-full max-w-[828px] px-5 text-center text-xs leading-relaxed text-gray-500 dark:text-gray-400 xl:px-7"
    >
      <p>{authAttributionLine(organisationName)}</p>
      <p className="mt-1">{authCopyrightLine()}</p>

      {destinations.length > 0 && (
        <ul className="mt-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
          {destinations.map((destination, index) => (
            <li key={destination.key} className="flex items-center gap-x-2">
              {index > 0 && (
                <span aria-hidden="true" className="text-gray-400">
                  &middot;
                </span>
              )}
              <LegalDocumentLink
                documentKey={destination.key}
                className="text-gray-600 hover:text-black dark:text-gray-300 dark:hover:text-white"
              />
            </li>
          ))}
        </ul>
      )}
    </footer>
  );
};

export default AuthLegalFooter;
